import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';

interface WidgetContextToolbarProps {
  selectedId: string;
  selectionBounds: { left: number; top: number; width: number; height: number; rotation?: number } | null;
  onEdit: () => void;
  onDelete: () => void;
  canvasScale: number;
  canvasRef?: React.RefObject<HTMLDivElement>;
  isCmsApp?: boolean;
  selectedCount?: number; // Safety prop to ensure we don't show when multiple selected
}

export const WidgetContextToolbar: React.FC<WidgetContextToolbarProps> = ({
  selectedId,
  selectionBounds,
  onEdit,
  onDelete,
  canvasScale,
  canvasRef,
  isCmsApp,
  selectedCount = 1
}) => {
  // Ultra-strict check to never show when more than one element is selected
  // This solves the issue where it "keeps on stays" or overlaps with grouping tools
  if (!selectionBounds || !selectedId || selectedCount > 1) {
    return null;
  }

  // Calculate toolbar position in screen coordinates
  const TOOLBAR_OFFSET = 12;
  const TOOLBAR_HEIGHT = 40; // Slightly smaller height now that labels are gone

  // Get canvas position in viewport if canvasRef is provided
  let canvasOffsetX = 0;
  let canvasOffsetY = 0;

  if (canvasRef?.current) {
    const canvasRect = canvasRef.current.getBoundingClientRect();
    canvasOffsetX = canvasRect.left;
    canvasOffsetY = canvasRect.top;
  }

  // Convert canvas coordinates to screen coordinates
  const screenX = canvasOffsetX + (selectionBounds.left * canvasScale);
  const screenY = canvasOffsetY + (selectionBounds.top * canvasScale);
  const screenWidth = selectionBounds.width * canvasScale;
  // const screenHeight = selectionBounds.height * canvasScale;

  // Position at top-center of selection, offset upward
  const top = screenY - TOOLBAR_OFFSET - TOOLBAR_HEIGHT;
  const left = screenX + screenWidth / 2;

  return (
    <div
      className="fixed z-[10001] flex items-center gap-1 bg-white rounded-full shadow-[0_12px_40px_rgba(0,0,0,0.15)] border border-gray-200 px-1.5 py-1.5 animate-in fade-in zoom-in-95 duration-200"
      style={{
        top: `${top}px`,
        left: `${left}px`,
        transform: `translateX(-50%) rotate(${selectionBounds.rotation || 0}deg)`,
        transformOrigin: 'center center',
        pointerEvents: 'auto'
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {!isCmsApp && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="p-1.5 rounded-full text-gray-700 hover:bg-green-50 hover:text-green-700 transition-all active:scale-95"
            title="Edit settings"
          >
            <Pencil className="w-4 h-4 text-[#30A84B]" />
          </button>

          <div className="w-px h-4 bg-gray-100 mx-0.5" />
        </>
      )}

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="p-1.5 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all active:scale-95"
        title="Delete element"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};
